import type { JSX, ReactNode } from 'react';

/**
 * The sidebar's glyph set.
 *
 * `SidebarLinkContent` falls back to the label's first character when a nav item
 * carries no icon, which is what this portal shipped: a rail of thirteen
 * letters, four of which were the same letter in one locale or the other
 * ("Readiness"/"Rates" as it was then named, "Alerts"/"Audit", and in French
 * "Qualite" beside "Quartiers"). A rail whose glyphs collide is a rail nobody
 * collapses, and which labels happen to collide changes with every rename -
 * which is the argument for every item carrying a glyph rather than for
 * choosing labels that do not clash.
 *
 * Drawn here rather than imported. The dependency budget (tech-stack section 9)
 * takes no icon library, and `@croissants/ui` deliberately ships no icon set -
 * these are this portal's vocabulary, not the design system's. One `Glyph`
 * wrapper holds every shared attribute so a new icon is a path and nothing else.
 */

function Glyph({ children }: { children: ReactNode }): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/**
 * One entry per sidebar destination, keyed by the nav item's own id so
 * `Shell.tsx` cannot pair a route with the wrong glyph and cannot omit one - the
 * key type is what the nav list is declared against.
 */
export const NAV_ICONS = {
  /** Dashboard - the live board. Four panels. */
  dashboard: (
    <Glyph>
      <rect x="2.75" y="2.75" width="6" height="6" rx="1.5" />
      <rect x="11.25" y="2.75" width="6" height="6" rx="1.5" />
      <rect x="2.75" y="11.25" width="6" height="6" rx="1.5" />
      <rect x="11.25" y="11.25" width="6" height="6" rx="1.5" />
    </Glyph>
  ),

  /**
   * Activity - a timeline: one rail, three moments on it, each with its entry.
   *
   * It was a bell while the destination was Alerts. A bell says "something
   * needs you", and four fifths of what this screen lists is a refusal, an
   * order event or an administrative act that needs nobody (OPS-027) - so the
   * glyph would have been promising an incident on every visit.
   */
  activity: (
    <Glyph>
      <path d="M5 3.75v12.5" />
      <circle cx="5" cy="6" r="1.35" />
      <circle cx="5" cy="10" r="1.35" />
      <circle cx="5" cy="14" r="1.35" />
      <path d="M8.75 6h7.5M8.75 10h5.5M8.75 14h6.5" />
    </Glyph>
  ),

  /** Orders - a receipt: the lines of one order and its torn foot. */
  orders: (
    <Glyph>
      <path d="M4.75 2.75h10.5v14.5l-2-1.25-1.75 1.25-1.75-1.25-1.75 1.25-1.75-1.25-1.5 1.25Z" />
      <path d="M7.75 7h4.5M7.75 10.25h4.5" />
    </Glyph>
  ),

  /**
   * Bakeries - a croissant: a domed crown, a belly hollowed under it, and a
   * horn where the two meet at each end.
   *
   * It was a shopfront under an awning with a door in it, which read as a house
   * and sat directly above Properties, the building glyph - two destinations,
   * one silhouette. Nothing else in this product is a croissant, which is
   * exactly what a rail glyph needs to be.
   *
   * ONE closed path, not two arcs. Drawn as two open strokes the ends stay
   * apart and the shape reads as an arch or a bridge - it has no horns, and the
   * horns are the whole difference between a croissant and a doorway. Closing
   * it makes the ends meet at a point, which is what a taper is at this size.
   *
   * No interior scoring either: at 20px with a 1.5 stroke the band between
   * crown and belly is about 3px, so a segment mark inside it merges with both
   * edges and the pastry fills in to a blob.
   */
  bakeries: (
    <Glyph>
      <path d="M3.4 14.8C3.4 6.8 16.6 6.8 16.6 14.8C13.8 10.4 6.2 10.4 3.4 14.8Z" />
    </Glyph>
  ),

  /** Properties - conciergeries and independent owners, i.e. buildings. */
  properties: (
    <Glyph>
      <path d="M3.75 17.25V4.5a1.25 1.25 0 0 1 1.25-1.25h6a1.25 1.25 0 0 1 1.25 1.25v12.75" />
      <path d="M12.25 8.75h3a1.25 1.25 0 0 1 1.25 1.25v7.25" />
      <path d="M2.5 17.25h15" />
      <path d="M6.5 6.75h1M9.5 6.75h1M6.5 10h1M9.5 10h1" />
      <path d="M7.25 17.25v-3.5h1.5v3.5" />
    </Glyph>
  ),

  /**
   * Devices - a handheld terminal: the body, the printer slot across the top
   * and the screen below it.
   *
   * Not a tablet and not a phone: the fleet this destination runs is Sunmi
   * counter hardware, and the slot is the one feature that says so at 20px.
   * Portrait and narrow, so it cannot be mistaken for `properties` beside it,
   * which is a wide building with a lower wing.
   */
  devices: (
    <Glyph>
      <rect x="5.5" y="2.5" width="9" height="15" rx="2" />
      <path d="M7.75 5.25h4.5" />
      <rect x="7.5" y="8" width="5" height="6" rx="0.75" />
    </Glyph>
  ),

  /** Finance - a currency sign: the S over a stem that runs past it top and bottom. */
  finance: (
    <Glyph>
      <path d="M10 2.5v15" />
      <path d="M14 4.5H8a2.75 2.75 0 0 0 0 5.5h4a2.75 2.75 0 0 1 0 5.5H6" />
    </Glyph>
  ),

  /**
   * Settings - two sliders, not a cog. The screen is a set of switches an
   * operator moves, and a cog is the glyph every product uses for the account
   * menu, which this portal already keeps in the top bar.
   *
   * The knobs sit at different offsets on purpose: two sliders aligned read as
   * an equals sign at 20px.
   */
  settings: (
    <Glyph>
      <path d="M2.75 6.75h3.5M9.75 6.75h7.5" />
      <circle cx="8" cy="6.75" r="1.75" />
      <path d="M2.75 13.25h7.5M13.75 13.25h3.5" />
      <circle cx="12" cy="13.25" r="1.75" />
    </Glyph>
  ),

  /** Quality and ratings - a star. */
  quality: (
    <Glyph>
      <path d="m10 2.75 2.35 4.76 5.25.77-3.8 3.7.9 5.23L10 14.74l-4.7 2.47.9-5.23-3.8-3.7 5.25-.77Z" />
    </Glyph>
  ),

  /** Coverage - a folded map, not a pin: this screen is areas, not addresses. */
  map: (
    <Glyph>
      <path d="m2.75 5.5 4.5-2.25 5.5 2.25 4.5-2.25v11.25l-4.5 2.25-5.5-2.25-4.5 2.25Z" />
      <path d="M7.25 3.25v11.25M12.75 5.5v11.25" />
    </Glyph>
  ),

  /**
   * Pilot - a door with a way out of it. The screen is a set of exits into the
   * other three portals, which is the one thing no other entry in this rail
   * does; a key or a badge would have said "sign-in", and the sign-in is not
   * what this screen is for.
   */
  pilot: (
    <Glyph>
      <path d="M11.25 2.75h4a1.5 1.5 0 0 1 1.5 1.5v11.5a1.5 1.5 0 0 1-1.5 1.5h-4" />
      <path d="M3.25 10h8.5" />
      <path d="m8.5 6.75 3.25 3.25-3.25 3.25" />
    </Glyph>
  ),

  /**
   * Support - a lifebuoy: the cases a person decides and the payments that
   * carry them out (issue #2272).
   */
  support: (
    <Glyph>
      <circle cx="10" cy="10" r="6.75" />
      <circle cx="10" cy="10" r="2.75" />
      <path d="M5.25 5.25l2.8 2.8M14.75 5.25l-2.8 2.8M5.25 14.75l2.8-2.8M14.75 14.75l-2.8-2.8" />
    </Glyph>
  ),

  /**
   * Debug - a wrench, for the entry holding Health status, Dead letter, Kill
   * switch and Audit log. A tool rather than a warning sign: the group is where
   * an operator goes to look into something, and three of its four sections are
   * read-only. Those four have no glyph of their own any more - the sub-tabs
   * are text, like Finance's.
   */
  debug: (
    <Glyph>
      <path d="M13.15 3.1a3.75 3.75 0 0 0-4.6 4.85l-4.6 4.6a1.75 1.75 0 0 0 2.5 2.5l4.6-4.6a3.75 3.75 0 0 0 4.85-4.6L14.1 8.65 11.35 5.9Z" />
    </Glyph>
  ),
} as const;

export type NavIconName = keyof typeof NAV_ICONS;
